import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* ── Types ── */
interface BudPiece {
  id: number;
  x: number;
  y: number;
  size: number;
  rotation: number;
  broken: boolean;
}

interface PaperParticle {
  x: number;
  y: number;
}

type GameStep = 'intro' | 'break' | 'spread' | 'tuck' | 'roll' | 'seal' | 'done';

const STEP_LABELS: Record<GameStep, string> = {
  intro: 'Ready',
  break: 'Break the Bud',
  spread: 'Spread on Paper',
  tuck: 'Tuck the Paper',
  roll: 'Roll It Up',
  seal: 'Seal It',
  done: 'Done',
};

const STEPS: GameStep[] = ['intro', 'break', 'spread', 'tuck', 'roll', 'seal', 'done'];

/* ── Helper: generate initial bud pieces ── */
function generateBud(): BudPiece[] {
  const pieces: BudPiece[] = [];
  for (let i = 0; i < 12; i++) {
    pieces.push({
      id: i,
      x: 120 + Math.random() * 80 - 40,
      y: 100 + Math.random() * 60 - 30,
      size: 18 + Math.random() * 14,
      rotation: Math.random() * 360,
      broken: false,
    });
  }
  return pieces;
}

export default function RealisticRollTab() {
  const [step, setStep] = useState<GameStep>('intro');
  const [budPieces, setBudPieces] = useState<BudPiece[]>(generateBud);
  const [brokenCount, setBrokenCount] = useState(0);
  const [spreadParticles, setSpreadParticles] = useState<PaperParticle[]>([]);
  const [tuckProgress, setTuckProgress] = useState(0);
  const [rollProgress, setRollProgress] = useState(0);
  const [sealProgress, setSealProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [rollScore, setRollScore] = useState(0);
  const [rollSpeeds, setRollSpeeds] = useState<number[]>([]);
  const lastRollTime = useRef(0);
  const areaRef = useRef<HTMLDivElement>(null);

  const stepIndex = STEPS.indexOf(step);
  const totalBudPieces = 12;

  // ── BREAK: Click/tap bud pieces to break them ──
  const breakPiece = useCallback((id: number) => {
    if (step !== 'break') return;
    setBudPieces(prev => prev.map(p =>
      p.id === id && !p.broken
        ? { ...p, broken: true, x: p.x + (Math.random() - 0.5) * 60, y: p.y + (Math.random() - 0.5) * 40, size: p.size * 0.6, rotation: Math.random() * 360 }
        : p
    ));
    setBrokenCount(c => {
      const next = c + 1;
      if (next >= totalBudPieces) setTimeout(() => setStep('spread'), 800);
      return next;
    });
  }, [step]);

  // ── SPREAD: Drag to scatter particles on paper ──
  const handleSpreadMove = useCallback((clientX: number, clientY: number) => {
    if (step !== 'spread' || !isDragging || !areaRef.current) return;
    const rect = areaRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    // Only add within the paper area (roughly center)
    if (x > 15 && x < 85 && y > 30 && y < 70) {
      setSpreadParticles(prev => {
        if (prev.length >= 60) {
          setTimeout(() => setStep('tuck'), 500);
          return prev;
        }
        return [...prev, { x: x + (Math.random() - 0.5) * 8, y: y + (Math.random() - 0.5) * 6 }];
      });
    }
  }, [step, isDragging]);

  // ── TUCK: Swipe up gesture ──
  const handleTuckMove = useCallback((clientY: number) => {
    if (step !== 'tuck' || !isDragging || !areaRef.current) return;
    const rect = areaRef.current.getBoundingClientRect();
    const progress = Math.max(0, Math.min(100, ((rect.bottom - clientY) / rect.height) * 150));
    setTuckProgress(progress);
    if (progress >= 95) {
      setStep('roll');
      setIsDragging(false);
    }
  }, [step, isDragging]);

  // ── ROLL: Drag left-to-right with speed tracking ──
  const handleRollMove = useCallback((clientX: number) => {
    if (step !== 'roll' || !isDragging || !areaRef.current) return;
    const rect = areaRef.current.getBoundingClientRect();
    const progress = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const now = Date.now();
    if (lastRollTime.current > 0) {
      const dt = now - lastRollTime.current;
      if (dt > 0) setRollSpeeds(prev => [...prev, 1000 / dt]);
    }
    lastRollTime.current = now;
    setRollProgress(progress);
    if (progress >= 95) {
      setStep('seal');
      setIsDragging(false);
    }
  }, [step, isDragging]);

  // ── SEAL: Drag across to lick/seal ──
  const handleSealMove = useCallback((clientX: number) => {
    if (step !== 'seal' || !isDragging || !areaRef.current) return;
    const rect = areaRef.current.getBoundingClientRect();
    const progress = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setSealProgress(progress);
    if (progress >= 90) {
      // Calculate final score
      let score = 70; // base
      if (rollSpeeds.length > 3) {
        const avg = rollSpeeds.reduce((a, b) => a + b, 0) / rollSpeeds.length;
        const variance = rollSpeeds.reduce((sum, s) => sum + Math.pow(s - avg, 2), 0) / rollSpeeds.length;
        const consistency = Math.max(0, 100 - Math.sqrt(variance) * 2);
        score = Math.round(consistency);
      }
      setRollScore(Math.min(100, Math.max(20, score)));
      setIsDragging(false);
      setStep('done');
    }
  }, [step, isDragging, rollSpeeds]);

  // ── Unified pointer handlers ──
  const onPointerDown = () => setIsDragging(true);
  const onPointerUp = () => setIsDragging(false);
  const onPointerMove = (e: React.PointerEvent) => {
    if (step === 'spread') handleSpreadMove(e.clientX, e.clientY);
    if (step === 'tuck') handleTuckMove(e.clientY);
    if (step === 'roll') handleRollMove(e.clientX);
    if (step === 'seal') handleSealMove(e.clientX);
  };

  // Reset game
  const resetGame = () => {
    setStep('intro');
    setBudPieces(generateBud());
    setBrokenCount(0);
    setSpreadParticles([]);
    setTuckProgress(0);
    setRollProgress(0);
    setSealProgress(0);
    setRollScore(0);
    setRollSpeeds([]);
    setIsDragging(false);
    lastRollTime.current = 0;
  };

  const gradeFromScore = (s: number) => {
    if (s >= 95) return 'A+';
    if (s >= 90) return 'A';
    if (s >= 80) return 'B+';
    if (s >= 70) return 'B';
    if (s >= 60) return 'C';
    if (s >= 50) return 'D';
    return 'F';
  };

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      <div className="flex items-center gap-2">
        {STEPS.filter(s => s !== 'intro').map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full transition-colors duration-300 ${
              STEPS.indexOf(s) <= stepIndex ? 'bg-[#4ade80]' : 'bg-white/10'
            }`} />
          </div>
        ))}
      </div>
      <p className="text-white/50 text-sm text-center font-medium">{STEP_LABELS[step]}</p>

      {/* Game Area */}
      <div
        ref={areaRef}
        className="relative w-full aspect-[4/3] max-w-lg mx-auto rounded-2xl border border-white/10 bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] overflow-hidden select-none touch-none"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onPointerMove={onPointerMove}
      >
        <AnimatePresence mode="wait">
          {/* INTRO */}
          {step === 'intro' && (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center gap-4"
            >
              <div className="text-center space-y-2">
                <p className="text-white font-bold text-lg">Realistic Roll</p>
                <p className="text-white/40 text-xs max-w-xs">Break the bud, spread it on paper, tuck, roll, and seal. Use your finger or mouse.</p>
              </div>
              <button
                onClick={() => setStep('break')}
                className="px-6 py-2.5 bg-[#4ade80]/15 border border-[#4ade80]/30 text-[#4ade80] font-semibold text-sm rounded-xl hover:bg-[#4ade80]/25 transition-colors"
              >
                Start Rolling
              </button>
            </motion.div>
          )}

          {/* BREAK THE BUD */}
          {step === 'break' && (
            <motion.div
              key="break"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0"
            >
              <p className="absolute top-3 left-0 right-0 text-center text-white/40 text-xs">
                Tap each piece to break it apart ({brokenCount}/{totalBudPieces})
              </p>
              {budPieces.map(p => (
                <motion.div
                  key={p.id}
                  onClick={() => breakPiece(p.id)}
                  animate={{
                    x: p.x,
                    y: p.y,
                    rotate: p.rotation,
                    scale: p.broken ? 0.7 : 1,
                    opacity: p.broken ? 0.5 : 1,
                  }}
                  whileTap={{ scale: 0.8 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  className="absolute cursor-pointer"
                  style={{ width: p.size, height: p.size }}
                >
                  <div className={`w-full h-full rounded-full ${
                    p.broken
                      ? 'bg-gradient-to-br from-[#2d5a1e] to-[#1a3a10]'
                      : 'bg-gradient-to-br from-[#4a8a2e] to-[#2d5a1e] shadow-lg shadow-green-900/30'
                  }`} />
                </motion.div>
              ))}
              {/* Progress ring */}
              <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                <div className="w-20 h-2 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-[#4ade80]/50 rounded-full"
                    animate={{ width: `${(brokenCount / totalBudPieces) * 100}%` }}
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* SPREAD ON PAPER */}
          {step === 'spread' && (
            <motion.div
              key="spread"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0"
            >
              <p className="absolute top-3 left-0 right-0 text-center text-white/40 text-xs">
                Drag across the paper to spread ({spreadParticles.length}/60)
              </p>
              {/* Paper */}
              <div className="absolute top-[25%] left-[10%] right-[10%] bottom-[25%] rounded-lg border border-[#d4a574]/20 bg-[#f5e6d0]/10" />
              {/* Particles */}
              {spreadParticles.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 0.8 }}
                  className="absolute w-1.5 h-1.5 rounded-full bg-[#4a8a2e]"
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                />
              ))}
              <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                <div className="w-20 h-2 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-[#4ade80]/50 rounded-full"
                    animate={{ width: `${(spreadParticles.length / 60) * 100}%` }}
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* TUCK */}
          {step === 'tuck' && (
            <motion.div
              key="tuck"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0"
            >
              <p className="absolute top-3 left-0 right-0 text-center text-white/40 text-xs">
                Drag upward to tuck the paper
              </p>
              {/* Paper visual */}
              <div className="absolute left-[15%] right-[15%] top-[30%] bottom-[20%]">
                <motion.div
                  className="w-full h-full rounded-lg border border-[#d4a574]/30 bg-[#f5e6d0]/10 origin-bottom"
                  animate={{ rotateX: tuckProgress * 0.6 }}
                  style={{ perspective: 400 }}
                />
                {/* Cannabis inside */}
                <div className="absolute inset-4 flex flex-wrap gap-1 items-center justify-center overflow-hidden">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <div key={i} className="w-2 h-2 rounded-full bg-[#4a8a2e]/60" />
                  ))}
                </div>
              </div>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                <div className="w-20 h-2 bg-white/5 rounded-full overflow-hidden">
                  <motion.div className="h-full bg-[#4ade80]/50 rounded-full" animate={{ width: `${tuckProgress}%` }} />
                </div>
              </div>
            </motion.div>
          )}

          {/* ROLL */}
          {step === 'roll' && (
            <motion.div
              key="roll"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0"
            >
              <p className="absolute top-3 left-0 right-0 text-center text-white/40 text-xs">
                Drag left to right — steady pace for best score
              </p>
              {/* Rolling joint visual */}
              <div className="absolute top-[35%] left-[10%] right-[10%] h-[30%] flex items-center">
                <motion.div
                  className="h-8 rounded-full bg-gradient-to-r from-[#d4a574]/30 to-[#d4a574]/60 border border-[#d4a574]/20 relative overflow-hidden"
                  animate={{ width: `${Math.max(10, rollProgress)}%` }}
                  transition={{ type: 'tween', duration: 0.05 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent" />
                  {/* Cannabis visible inside */}
                  <div className="absolute inset-1 flex gap-0.5 items-center overflow-hidden">
                    {Array.from({ length: Math.floor(rollProgress / 5) }).map((_, i) => (
                      <div key={i} className="w-1 h-1 rounded-full bg-[#4a8a2e]/40 shrink-0" />
                    ))}
                  </div>
                </motion.div>
              </div>
              {/* Speed feedback */}
              <div className="absolute bottom-8 left-0 right-0 text-center">
                {rollSpeeds.length > 2 && (() => {
                  const recent = rollSpeeds.slice(-5);
                  const avg = recent.reduce((a, b) => a + b, 0) / recent.length;
                  if (avg > 80) return <p className="text-red-400 text-xs">Too fast! Slow down.</p>;
                  if (avg < 15) return <p className="text-yellow-400 text-xs">Too slow! Speed up a bit.</p>;
                  return <p className="text-[#4ade80] text-xs">Perfect pace!</p>;
                })()}
              </div>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                <div className="w-20 h-2 bg-white/5 rounded-full overflow-hidden">
                  <motion.div className="h-full bg-[#4ade80]/50 rounded-full" animate={{ width: `${rollProgress}%` }} />
                </div>
              </div>
            </motion.div>
          )}

          {/* SEAL */}
          {step === 'seal' && (
            <motion.div
              key="seal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0"
            >
              <p className="absolute top-3 left-0 right-0 text-center text-white/40 text-xs">
                Drag across to seal it
              </p>
              {/* Joint shape */}
              <div className="absolute top-[35%] left-[10%] right-[10%] flex items-center justify-center">
                <div className="relative w-full h-10">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#d4a574]/40 to-[#d4a574]/60 border border-[#d4a574]/30" />
                  {/* Seal line */}
                  <motion.div
                    className="absolute top-0 h-full rounded-full bg-[#d4a574]/30 border-t border-[#c49564]/40"
                    animate={{ width: `${sealProgress}%` }}
                    style={{ left: 0 }}
                  />
                  {/* Moisture effect */}
                  <motion.div
                    className="absolute top-0 h-1 bg-white/10 rounded-full"
                    animate={{ width: `${sealProgress}%`, opacity: sealProgress > 0 ? 0.3 : 0 }}
                    style={{ left: 0 }}
                  />
                </div>
              </div>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                <div className="w-20 h-2 bg-white/5 rounded-full overflow-hidden">
                  <motion.div className="h-full bg-[#4ade80]/50 rounded-full" animate={{ width: `${sealProgress}%` }} />
                </div>
              </div>
            </motion.div>
          )}

          {/* DONE */}
          {step === 'done' && (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute inset-0 flex flex-col items-center justify-center gap-4"
            >
              {/* Final joint visual */}
              <div className="relative">
                <motion.div
                  initial={{ rotate: -5 }}
                  animate={{ rotate: 0 }}
                  className="w-48 h-6 rounded-full bg-gradient-to-r from-[#c49564] via-[#d4a574] to-[#c49564] border border-[#b88a5a]/40 shadow-lg shadow-[#d4a574]/10 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent rounded-full" />
                  {/* Filter tip */}
                  <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[#d4a574] to-[#c49564] rounded-l-full border-r border-[#b88a5a]/30">
                    <div className="absolute inset-0.5 rounded-l-full bg-[#e8d5b8]/20" />
                  </div>
                  {/* Twist end */}
                  <div className="absolute right-0 top-1 bottom-1 w-4 bg-[#c49564] rounded-r-full" />
                </motion.div>
              </div>

              {/* Score */}
              <div className="text-center space-y-1">
                <p className="text-white/40 text-xs uppercase tracking-wider">Roll Score</p>
                <p className={`text-4xl font-bold ${rollScore >= 80 ? 'text-[#4ade80]' : rollScore >= 60 ? 'text-[#d4a574]' : 'text-red-400'}`}>
                  {gradeFromScore(rollScore)}
                </p>
                <p className="text-white/50 text-sm">{rollScore}/100</p>
                <p className="text-white/30 text-xs">
                  {rollScore >= 90 ? 'Master roller!' : rollScore >= 70 ? 'Solid roll.' : rollScore >= 50 ? 'Could be tighter.' : 'Needs practice.'}
                </p>
              </div>

              <button
                onClick={resetGame}
                className="px-5 py-2 bg-white/5 border border-white/10 text-white/60 text-sm rounded-xl hover:bg-white/10 transition-colors"
              >
                Roll Again
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Instructions */}
      <div className="text-center">
        <p className="text-white/20 text-xs">
          {step === 'break' && 'Tap each green piece to break the bud apart'}
          {step === 'spread' && 'Hold and drag across the paper to spread the ground cannabis'}
          {step === 'tuck' && 'Hold and drag upward to tuck the paper edge'}
          {step === 'roll' && 'Hold and drag left-to-right at a steady pace'}
          {step === 'seal' && 'Hold and drag across to lick and seal the joint'}
        </p>
      </div>
    </div>
  );
}
